import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FilterHeader = ({ onFiltersChange }) => {
  const [activeFilters, setActiveFilters] = useState({
    cohort: 'all',
    treatment: 'all',
    period: '30d',
    comparison: 'mom'
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const cohortOptions = [
    { value: 'all', label: 'All Patients' },
    { value: 'inpatient', label: 'Inpatients' },
    { value: 'outpatient', label: 'Outpatients' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'icu', label: 'ICU Patients' }
  ];

  const treatmentOptions = [
    { value: 'all', label: 'All Treatments' },
    { value: 'surgical', label: 'Surgical' },
    { value: 'medical', label: 'Medical' },
    { value: 'preventive', label: 'Preventive' },
    { value: 'rehabilitation', label: 'Rehabilitation' }
  ];

  const periodOptions = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '6m', label: 'Last 6 Months' },
    { value: '1y', label: 'Last Year' }
  ];

  const comparisonOptions = [
    { value: 'mom', label: 'Month over Month' },
    { value: 'yoy', label: 'Year over Year' },
    { value: 'baseline', label: 'vs Baseline' },
    { value: 'benchmark', label: 'vs Benchmark' }
  ];

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...activeFilters, [filterType]: value };
    setActiveFilters(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const resetFilters = () => {
    const defaultFilters = {
      cohort: 'all',
      treatment: 'all',
      period: '30d',
      comparison: 'mom'
    };
    setActiveFilters(defaultFilters);
    if (onFiltersChange) {
      onFiltersChange(defaultFilters);
    }
  };

  const getActiveFilterCount = () => {
    return Object.values(activeFilters)?.filter(value => value !== 'all' && value !== '30d' && value !== 'mom')?.length;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 medical-shadow-sm mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} color="var(--color-primary)" />
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Analytics Filters</h3>
            <p className="text-sm text-text-secondary">Customize your patient care analysis</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {getActiveFilterCount() > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-text-secondary">
                {getActiveFilterCount()} active filter{getActiveFilterCount() !== 1 ? 's' : ''}
              </span>
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="RotateCcw" size={14} className="mr-1" />
                Reset
              </Button>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
          </Button>
        </div>
      </div>
      {/* Quick Filters - Always Visible */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Patient Cohort</label>
          <select
            value={activeFilters?.cohort}
            onChange={(e) => handleFilterChange('cohort', e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {cohortOptions?.map((option) => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Treatment Type</label>
          <select
            value={activeFilters?.treatment}
            onChange={(e) => handleFilterChange('treatment', e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {treatmentOptions?.map((option) => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Time Period</label>
          <select
            value={activeFilters?.period}
            onChange={(e) => handleFilterChange('period', e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {periodOptions?.map((option) => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Comparison</label>
          <select
            value={activeFilters?.comparison}
            onChange={(e) => handleFilterChange('comparison', e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {comparisonOptions?.map((option) => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Advanced Filters - Expandable */}
      {isExpanded && (
        <div className="border-t border-border pt-4">
          <h4 className="text-sm font-semibold text-text-primary mb-3">Advanced Filters</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Age Range</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-text-primary text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <span className="text-text-secondary">-</span>
                <input
                  type="number"
                  placeholder="Max"
                  className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-text-primary text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Risk Level</label>
              <div className="space-y-2">
                {['Low Risk', 'Medium Risk', 'High Risk']?.map((risk) => (
                  <label key={risk} className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded border-border text-primary focus:ring-primary" />
                    <span className="text-sm text-text-primary">{risk}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Department</label>
              <select className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary text-sm focus:ring-2 focus:ring-primary focus:border-transparent">
                <option value="">All Departments</option>
                <option value="cardiology">Cardiology</option>
                <option value="neurology">Neurology</option>
                <option value="orthopedics">Orthopedics</option>
                <option value="oncology">Oncology</option>
              </select>
            </div>
          </div>
        </div>
      )}
      {/* Filter Summary */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-4 text-sm text-text-secondary">
          <div className="flex items-center space-x-1">
            <Icon name="Users" size={14} />
            <span>2,847 patients in current selection</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Clock" size={14} />
            <span>Data updated: {new Date()?.toLocaleTimeString('en-US', { 
              hour12: false, 
              hour: '2-digit', 
              minute: '2-digit' 
            })}</span>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <Icon name="Download" size={14} className="mr-1" />
            Export
          </Button>
          <Button variant="outline" size="sm">
            <Icon name="Bookmark" size={14} className="mr-1" />
            Save View
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterHeader;